/** @format */

import React, { FC, useState } from 'react'
import { _$, Card, Dialog, Editable, Button, Icon, Message } from '@king-design/react'
import { useHistory } from 'react-router'
import styled from 'styled-components'
import { MonitorSearch, TablePage, Divider, CollapsibleList } from 'src/components'
import { nameValidate } from 'src/utils/formValidate'
import useFormTable from 'src/hooks/useFormTable'
import useTableGroup from 'src/hooks/useTableGroup'
import API, { modifyTemplate, deleteTemplate } from 'src/api/ApiAlarmTemplate'
import { GetListSysEvents } from 'src/api/ApiAlarmServer'
import { renderTriggerRule } from 'src/containers/MonitorAlarmTactics/TacticsList'
import { useAsync } from 'react-use'
import { handleParamsCase } from 'src/utils/common'
import ReferencedDialog from './ReferencedDialog'
interface Props {
  className?: string
}

const MonitorAlarmTemplate: FC<Props> = ({ className }) => {
  const history = useHistory()
  const productList = useTableGroup('productList')
  const [tableGroup, setTableGroup] = useState({
    ProductType: 999
  })
  const [query, setQuery] = useState<string>('')
  // 已绑定告警
  const [referencedVisible, setReferencedVisible] = useState<boolean>(false)
  const [referencedParams, setReferencedParams] = useState<any>()

  // 获取事件列表
  const { value: eventList } = useAsync(async () => {
    const result = await GetListSysEvents({
      ProductType: 999
    })
    if (result && result?.code === 'Success' && result?.data.eventList) {
      const eventList = result.data.eventList
      return eventList
    } else {
      Message.error(result?.message)
      return null
    }
  }, [])

  const { run, result, total, pageState, loading }: any = useFormTable(
    API.getTemplateList,
    {
      params: {
        ProductType: tableGroup.ProductType,
        TemplateName: query
      }
    },
    data => {
      return {
        tableData: data?.Templates || [],
        totalCount: data?.TotalCount || 0
      }
    }
  )

  // 获取事件名称
  const getEventName = (productType, value) => {
    if (eventList?.length) {
      return (
        eventList.find(item => item.productType === productType && item.name === value)?.nameDesc ||
        ''
      )
    } else {
      return ''
    }
  }

  const scheme = {
    TemplateName: {
      width: 200,
      title: _$('模版名称'),
      template: function (item) {
        return (
          <div className="policyType_cell">
            <Editable
              value={item.TemplateName}
              validate={value => nameValidate(value)}
              onChangeValue={async value => {
                const res = await modifyTemplate({
                  ...item,
                  TemplateName: value
                })
                if (res) {
                  run()
                }
              }}
            >
              <p className="metric-legend">{item.TemplateName}</p>
            </Editable>
          </div>
        )
      }
    },
    TriggerRules: {
      title: _$('触发条件'),
      template: function ({ ProductType, TriggerRules, EventRules }) {
        const allEvent = eventList?.filter(item => item.productType === ProductType)
        return (
          <>
            {TriggerRules?.length ? (
              <div className="trigger_rules">
                <h4>阈值告警：</h4>
                <CollapsibleList
                  defaultCount={3}
                  data={handleParamsCase(TriggerRules, 'lower')}
                  renderItem={item => renderTriggerRule(ProductType, item)}
                />
              </div>
            ) : null}
            {EventRules.length ? (
              <div className="trigger_rules">
                <h4>事件告警：</h4>
                <div>
                  {EventRules?.length === allEvent?.length ? (
                    <div>{_$('全部事件')}</div>
                  ) : (
                    <CollapsibleList<{ EventName: string }>
                      defaultCount={3}
                      data={EventRules}
                      renderItem={item => <div>{getEventName(ProductType, item.EventName)}</div>}
                      getItemKey={item => item.EventName}
                    />
                  )}
                </div>
              </div>
            ) : null}
          </>
        )
      }
    },
    ProductType: {
      title: _$('产品类型'),
      width: 150,
      group: productList || [],
      template: function (item) {
        const productName =
          productList?.find(({ value }) => value === item.ProductType)?.label || ''
        return <span>{productName}</span>
      }
    },
    Remark: {
      width: 150,
      title: _$('模板描述'),
      template: function (item) {
        return <span>{item.Remark || '-'}</span>
      }
    },
    ThresholdReferencedPolicies: {
      width: 100,
      title: _$('绑定策略数'),
      template: function (item) {
        const { ThresholdReferencedPolicies, EventReferencedPolicies } = item
        const total = ThresholdReferencedPolicies + EventReferencedPolicies
        return total > 0 ? (
          <a
            onClick={() => {
              setReferencedParams(item)
              setReferencedVisible(true)
            }}
          >
            {total}
          </a>
        ) : (
          0
        )
      }
    },
    LastUpdatedAt: {
      width: 100,
      title: _$('最后修改时间')
    },
    Action: {
      title: _$('操作'),
      width: 120,
      template: item => {
        const { TemplateID } = item
        return (
          <div className="ksc-space">
            <a
              onClick={() => {
                history.push(`/monitor/alarmServer/template/edit/${TemplateID}`)
              }}
            >
              {_$('编辑')}
            </a>
            <Divider />
            {/* 删除 */}
            <a
              onClick={() => {
                Dialog.confirm({
                  title: '确定删除所选触发条件模板',
                  content: '确定删除所选触发条件模板?'
                }).then(async () => {
                  const result = await deleteTemplate(TemplateID)
                  if (result) {
                    run({ PageIndex: 1 })
                    Message.success('删除成功')
                  }
                })
              }}
            >
              {_$('删除')}
            </a>
            {/* )} */}
          </div>
        )
      }
    }
  }

  return (
    <div className={className}>
      <Card>
        <div className="header">
          <Button
            type="primary"
            onClick={() => {
              history.push('/monitor/alarmServer/template/edit')
            }}
          >
            <Icon className="ion-plus-round"></Icon>
            新建触发条件模版
          </Button>
          <div className="query-box">
            <MonitorSearch
              placeholder={_$('请输入模版名称')}
              value={query}
              onChange={value => {
                setQuery(value)
                run({
                  PageIndex: 1,
                  ProductType: tableGroup.ProductType,
                  Query: value
                })
              }}
            />
          </div>
        </div>
        <div className="marginTop">
          {scheme ? (
            <TablePage
              tableProps={{
                scheme: scheme,
                data: result || [],
                rowKey: value => value?.TemplateID,
                checkType: 'none',
                resizable: true,
                group: tableGroup,
                onChangeGroup: value => {
                  setTableGroup(value)
                  run({
                    PageIndex: 1,
                    ProductType: value.ProductType,
                    TemplateName: query
                  })
                }
              }}
              run={run}
              loading={loading}
              pageProps={{ total, ...pageState }}
            />
          ) : null}
        </div>
        <ReferencedDialog
          visible={referencedVisible}
          templateData={referencedParams}
          onClose={update => {
            if (update) {
              run()
            }
            setReferencedVisible(false)
            setReferencedParams(undefined)
          }}
        />
      </Card>
    </div>
  )
}

export default styled(MonitorAlarmTemplate)`
  .header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .policyType_cell {
    display: flex;
    align-items: center;
    .k-input {
      width: 100%;
    }
    .k-editable {
      .c-ellipsis {
        white-space: normal;
      }
    }
    .metric-legend {
      max-width: 100%;
      margin: 0;
      word-break: break-all;
    }
  }
  .trigger_rules {
    &:last-child {
      margin-top: 4px;
    }
  }
`
